import { isBrowser } from '../../core/runtimes/detect';
import { LogEntry } from '../../core/logEntry';
import { PluginFunction, PluginOptions } from './plugin';

const DEFAULT_BATCH_LIMIT = 20;
const DEFAULT_DEBOUNCE = 1000 * 10; // 10 seconds
const MAX_STACK_SIZE = 250;

type BeaconConfiguration = {
  endpoint: string;
  batchLimit?: number;
  debounce?: number;
};

class BeaconTransport {
  private stack: LogEntry[];

  private endpoint: string;
  private batchLimit: number;
  private debounce: number;
  private currentTimeout?: number;
  private sendBeacon: (url: string | URL, data: string) => boolean;

  constructor({ endpoint, batchLimit = DEFAULT_BATCH_LIMIT, debounce = DEFAULT_DEBOUNCE }: BeaconConfiguration) {
    this.endpoint = endpoint;
    this.batchLimit = batchLimit;
    this.debounce = debounce;
    this.stack = [];

    if (isBrowser()) {
      this.sendBeacon = window.navigator.sendBeacon.bind(window.navigator);
      this.start();
    } else {
      this.sendBeacon = () => false;
    }
  }

  private sendAll() {
    while (this.stack.length > 0) {
      this.send();
    }
  }

  private send() {
    const messageStack = this.stack.splice(0, this.batchLimit);
    if (messageStack.length < 1) {
      return;
    }

    // FIXME: We can't log errors about plugin behavior in the current model.
    // FIXME: Data loss can happen right here, but memory leaks can't as a result.
    // const queued = this.sendBeacon(this.endpoint, JSON.stringify(messageStack));
    // if (!queued) {
    //   this.emit('error', new Error('sendBeacon returned false. Stack not queued.'));
    // }
    this.sendBeacon(this.endpoint, JSON.stringify(messageStack));
  }

  private next() {
    this.currentTimeout = window.setTimeout(() => {
      this.send();
      this.next();
    }, this.debounce);
  }

  private start() {
    window.document.addEventListener('visibilitychange', () => {
      if (window.document.visibilityState === 'hidden') {
        this.sendAll();
        window.clearTimeout(this.currentTimeout);
      }
    });
    this.next();
  }

  // FIXME: We can't log errors about plugin behavior in the current model.
  log(entry: LogEntry) {
    // Try to serialize and add to the stack to send when we're ready.
    try {
      // Put the newest value on top
      this.stack.unshift(entry);

      // If we've reached our max stack size, purge older events.
      // This is to protect endpoints from runaway loggers.
      // const discard = this.stack.splice(MAX_STACK_SIZE);
      // if (discard.length) {
      //   this.emit('warn', `${discard.length} events discarded`);
      // }

      // this.emit('logged', entry);

      // FIXME: Once we can emit, use the old version of this code.
      // Until then, this prevents from stack sizes growing out of control.
      this.stack.splice(MAX_STACK_SIZE);
    } catch (e) {
      // this.emit('warn', e);
    }
  }
}

/**
 * Beacon is a loglevel plugin used to send log events to a specific endpoint.
 *
 * Important: It's a terminal plugin. No further plugins or logging will
 * be called after its execution.
 */
const Beacon: (transport: BeaconTransport) => PluginFunction =
  (transport) =>
  ({ message, methodName }: PluginOptions) => {
    if (typeof message === 'object') {
      const level = methodName;
      transport.log({ ...message, level });
    }
  };

export type { BeaconConfiguration };
export { BeaconTransport, Beacon };
